'use client';

import { type FC } from 'react';
import { cn } from '../../../utils/cn';
import { Spinner, EmptyState } from '../../ui';
import { Credits } from '../../Credits';
import { ScreenContent, ScreenFixedTop } from '../../../screen';
import { RushMidTabsBar } from './shared/RushMidTabsBar';
import type { RushMidTab } from './RushMidGame';
import { CardBackdrop, MiniSide } from './cardParts';
import type { OpenSeat } from './OpenGameScreen';
import { useRushMidHistory } from '../../../queries/useRushMidQueries';
import { useLocalTranslation } from '../../../hooks/useLocalTranslation';
import type { Language } from '../../../i18n/types';
import { formatDayMonth } from '../../../i18n/formatDate';
import translations from './RushMid.translations';
import type { RushMidHistoryItem, RushMidHistorySeat } from '../../../services/generated/Api';

type Translate = ReturnType<typeof useLocalTranslation>['t'];

const MODE_KEYS: Record<number, 'mode1' | 'mode2' | 'mode5'> = {
  1: 'mode1',
  2: 'mode2',
  5: 'mode5',
};

export function modeLabel(mode: number, t: Translate): string {
  const key = MODE_KEYS[mode];
  return key ? t(key) : t('modeN', { size: mode });
}

/** Relative date ("2 ч назад", "3 дн назад") falling back to an absolute date. */
function shortDate(iso: string, t: Translate, lang: Language): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffMs = Date.now() - then;
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return t('timeJustNow');
  if (min < 60) return t('timeMinAgo', { min });
  const hours = Math.floor(min / 60);
  if (hours < 24) return t('timeHoursAgo', { hours });
  const days = Math.floor(hours / 24);
  if (days < 7) return t('timeDaysAgo', { days });
  return formatDayMonth(iso, lang);
}

export interface HistoryScreenProps {
  /** When provided the hook is skipped (Storybook / tests). */
  items?: RushMidHistoryItem[];
  tab?: RushMidTab;
  onTab?: (tab: RushMidTab) => void;
  onOpen?: (matchId: string) => void;
}

export const HistoryScreen: FC<HistoryScreenProps> = ({ items: itemsProp, tab, onTab, onOpen }) => {
  const { t, lang } = useLocalTranslation(translations);
  const query = useRushMidHistory(itemsProp === undefined);
  const items = itemsProp ?? query.data?.items;
  const isLoading = itemsProp === undefined && query.isLoading;

  return (
    <>
    <ScreenFixedTop title={t('lobbyTitle')}>
      {tab && onTab && <RushMidTabsBar activeTab={tab} onChange={onTab} />}
    </ScreenFixedTop>
    <ScreenContent hideLegalSection scrollKey="rush-mid-history">
    <div className="flex min-h-full flex-col px-4 pt-3 pb-6">
      <div className="flex-1">
        {isLoading ? (
          <div className="flex min-h-40 items-center justify-center">
            <Spinner size="l" />
          </div>
        ) : !items || items.length === 0 ? (
          <EmptyState
            className="mt-6"
            title={t('historyEmptyTitle')}
            description={t('historyEmptyDescription')}
          />
        ) : (
          <ul className="flex flex-col gap-2">
            {items.map((m) => (
              <HistoryRow key={m.id} item={m} t={t} lang={lang} onOpen={onOpen} />
            ))}
          </ul>
        )}
      </div>
    </div>
    </ScreenContent>
    </>
  );
};

/** History rosters map onto the same `OpenSeat` strip the games list uses: real players carry
 *  their avatar; a bot seat has no id, so it renders the generic fallback with no image fetch. */
const toSeats = (seats: RushMidHistorySeat[], botLabel: string): OpenSeat[] =>
  seats.map((s) => ({ userId: s.userId ?? '', name: s.bot ? botLabel : s.name, level: 0, bot: s.bot }));

const GLASS_PILL = 'rounded-full px-2.5 py-1 shadow-[inset_0_1px_0_rgba(255,255,255,0.22)] backdrop-blur-md';

/** The finished-match card — the games-list card design (map cover, ticket·mode header, VS
 *  avatar strip, glassy footer) with the outcome (result badge + net coins) in place of the
 *  join affordance. Tapping opens the match detail / replay. */
const HistoryRow: FC<{ item: RushMidHistoryItem; t: Translate; lang: Language; onOpen?: (matchId: string) => void }> = ({ item, t, lang, onOpen }) => {
  const { matchId, mode, ticket, map, net, won, draw, teams, createdAt } = item;
  const botLabel = t('botShort');
  return (
    <li>
      <button
        type="button"
        onClick={() => onOpen?.(matchId)}
        disabled={!onOpen}
        className="relative flex w-full flex-col gap-3 overflow-hidden rounded-2xl bg-bg-secondary p-4 text-start transition-opacity active:opacity-80 disabled:active:opacity-100"
      >
        <CardBackdrop map={map} />
        <div className="relative flex items-center justify-between gap-2">
          <span className="inline-flex items-center gap-1 text-base font-extrabold text-white">
            <Credits amount={ticket} iconSize={15} /> · {modeLabel(mode, t)}
          </span>
          <span
            className={cn(
              'text-[11px] font-semibold',
              GLASS_PILL,
              draw ? 'bg-white/10 text-white/85' : won ? 'bg-accent-green/25 text-accent-green' : 'bg-accent-red/25 text-accent-red',
            )}
          >
            {draw ? t('matchDraw') : won ? t('win') : t('defeat')}
          </span>
        </div>
        <div className="relative flex min-w-0 items-center justify-center gap-2">
          <MiniSide side="t" seats={toSeats(teams.t, botLabel)} />
          <span className="shrink-0 text-xs font-bold text-white/70">VS</span>
          <MiniSide side="ct" seats={toSeats(teams.ct, botLabel)} />
        </div>
        <div className="relative flex items-center justify-between gap-2">
          <span
            className={cn(
              'inline-flex items-center gap-1 text-xs font-semibold text-white',
              GLASS_PILL,
              draw ? 'bg-white/10' : net >= 0 ? 'bg-accent-green/25' : 'bg-accent-red/25',
            )}
          >
            {draw ? (
              t('drawRefund')
            ) : (
              <>{net >= 0 ? '+' : '−'}<Credits amount={Math.abs(net)} iconSize={12} /></>
            )}
          </span>
          <span className={cn('text-[11px] font-medium text-white/70 bg-white/10', GLASS_PILL)}>{shortDate(createdAt, t, lang)}</span>
          <span className={cn('text-[11px] font-bold uppercase tracking-wider text-white bg-white/10', GLASS_PILL)}>
            {map}
          </span>
        </div>
      </button>
    </li>
  );
};
